/**
 * Canonical address for contacts in app state: **`email`** only.
 * API/LDAP may send `mail` — merge here or at ingress (e.g. flatten) so the rest of the app reads `email` only.
 */

/** Fields that may carry an address before normalization (`email` preferred, `mail` from API/LDAP). */
export interface ContactEmailSource {
  email?: string | null;
  mail?: string | null;
}

/** Same as {@link ContactEmailSource} plus optional `uid`. */
export interface ContactIdentifierSource extends ContactEmailSource {
  uid?: string | null;
}

/**
 * Prefer `email`, else legacy/API `mail`.
 * Always returns a string (never null/undefined); use `''` when neither field is present — callers
 * treat falsy as "no address" without changing the public contract to `string | null`.
 */
export function resolveContactEmail(c: ContactEmailSource): string {
  return (c.email ?? c.mail ?? '').trim();
}

/**
 * Merge email/mail from `patch` over `base` without letting explicit `undefined` in `patch` wipe
 * `base` when using object spread (`{ ...base, ...patch }`).
 */
export function mergeContactEmailFields(
  base: ContactEmailSource,
  patch: ContactEmailSource
): ContactEmailSource {
  return {
    email: patch.email !== undefined ? patch.email : base.email,
    mail: patch.mail !== undefined ? patch.mail : base.mail
  };
}
